import { useGetFile } from "@/queries/files/file.api";
import type {
  LogItemType,
  SuspensionExtensionEvent,
} from "@/types/common/logs";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import dayjs from "dayjs";

// ──────────────────────────────────────────────
// Title builder
// ──────────────────────────────────────────────

function getLogTitle(log: LogItemType): string {
  const ctx = log.properties?.context;
  const nodeName = ctx?.node_name || "Nodo";
  const normalizedEvent = (log.event || log.properties?.action || "")
    .toLowerCase()
    .trim();
  const requestType =
    ctx?.request_type ||
    (normalizedEvent.includes("extension") ? "extension" : "suspension");
  const requestLabel = requestType === "extension" ? "prórroga" : "suspensión";
  const event = normalizedEvent as SuspensionExtensionEvent;

  // Determine role from event name or causer_type
  switch (event) {
    // ── Created ──
    case "node_deadline_suspension_request":
    case "node_time_extension_request":
    case "suspension_request_created_external":
    case "suspension_request_created_internal":
    case "extension_request_created":
      return `El tramitador ha hecho una solicitud de ${requestLabel} en el paso "${nodeName}"`;

    // ── Scaled ──
    case "suspension_request_scaled":
    case "extension_request_scaled":
      return `Analista envió solicitud de ${requestLabel} al líder en el paso "${nodeName}"`;

    // ── Analyst approved / denied ──
    case "suspension_request_analyst_approved":
    case "extension_request_analyst_approved":
      return `Analista aprobó solicitud de ${requestLabel} en el paso "${nodeName}"`;
    case "suspension_request_analyst_denied":
    case "extension_request_analyst_denied":
      return `Analista denegó solicitud de ${requestLabel} en el paso "${nodeName}"`;

    // ── Leader approved / denied ──
    case "suspension_request_leader_approved":
    case "extension_request_leader_approved":
      return `Líder aprobó solicitud de ${requestLabel} en el paso "${nodeName}"`;
    case "suspension_request_leader_denied":
    case "extension_request_leader_denied":
      return `Líder denegó solicitud de ${requestLabel} en el paso "${nodeName}"`;

    // ── Generic approved / denied (infer role from context) ──
    case "suspension_request_approved":
    case "extension_request_approved": {
      const role = inferRole(log);
      return `${role} aprobó solicitud de ${requestLabel} en el paso "${nodeName}"`;
    }
    case "suspension_request_denied":
    case "extension_request_denied": {
      const role = inferRole(log);
      return `${role} denegó solicitud de ${requestLabel} en el paso "${nodeName}"`;
    }

    default:
      return log.description || "Actividad registrada";
  }
}

/**
 * Infer role (Analista / Líder) from context.
 * Checks explicit role fields first, then scope, then description hints.
 */
function inferRole(log: LogItemType): string {
  const ctx = log.properties?.context;
  const desc = (log.description || "").toLowerCase();

  // Explicit role field from backend
  const explicitRole = (
    ((ctx as Record<string, unknown>)?.role as string) ?? ""
  ).toLowerCase();
  if (explicitRole === "leader" || explicitRole === "líder") return "Líder";
  if (explicitRole === "analyst" || explicitRole === "analista")
    return "Analista";

  // Internal suspension requests go directly to leader
  if (ctx?.request_scope === "internal") return "Líder";

  // If the request was previously scaled to a leader before approval
  if (
    (ctx as Record<string, unknown>)?.scaled_at ||
    (ctx as Record<string, unknown>)?.scaled_by
  ) {
    return "Líder";
  }

  // Description hints
  if (desc.includes("leader") || desc.includes("líder")) return "Líder";

  return "Analista";
}

// ──────────────────────────────────────────────
// Status helpers
// ──────────────────────────────────────────────

type LogStatus = "approved" | "denied" | "scaled" | "created";

function getStatus(log: LogItemType): LogStatus {
  const ctx = log.properties?.context;
  const event = (log.event || log.properties?.action || "")
    .toLowerCase()
    .trim();

  if (ctx?.status) return ctx.status as LogStatus;
  if (event.includes("approved")) return "approved";
  if (event.includes("denied")) return "denied";
  if (event.includes("scaled")) return "scaled";
  return "created";
}

function getApprovedDays(log: LogItemType): number | null {
  const ctx = log.properties?.context;
  return (
    ctx?.approved_days ?? ctx?.requested_days ?? ctx?.requested_amount ?? null
  );
}

function getUnit(log: LogItemType): string {
  const ctx = log.properties?.context;
  const unit = ctx?.requested_unit || "day";
  const amount = getApprovedDays(log) ?? 0;

  const unitMap: Record<string, [string, string]> = {
    day: ["día", "días"],
    week: ["semana", "semanas"],
    month: ["mes", "meses"],
    year: ["año", "años"],
    hour: ["hora", "horas"],
    minute: ["minuto", "minutos"],
  };

  const [singular, plural] = unitMap[unit] || ["día", "días"];
  return amount === 1 ? singular : plural;
}

// ──────────────────────────────────────────────
// Date formatter
// ──────────────────────────────────────────────

function formatDate(date: Date): string {
  const d = dayjs(date);
  const day = d.format("DD");
  const month = d.format("MMM");
  const rest = d.format(", YYYY | HH:mm");
  return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)}${rest}`;
}

// ──────────────────────────────────────────────
// Comment colors by status
// ──────────────────────────────────────────────

function getCommentStyle(log: LogItemType) {
  const status = getStatus(log);
  const event = (log.event || log.properties?.action || "")
    .toLowerCase()
    .trim();

  if (status === "denied") {
    const isLeader = event.includes("leader") || inferRole(log) === "Líder";
    return {
      iconColor: "text-warning-quiet",
      labelColor: "text-warning",
      label: isLeader ? "Comentario del líder:" : "Comentario:",
    };
  }

  // All statuses (created, approved, scaled) share the same orange style
  return {
    iconColor: "text-warning-quiet",
    labelColor: "text-warning",
    label: "Comentario:",
  };
}

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────

interface SuspensionExtensionLogProps {
  log: LogItemType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SubjectFile = Record<string, any>;

interface ResolvedFile {
  name: string;
  uuid?: string;
  url?: string;
}

/** Normalise a file entry from subject.files into a ResolvedFile */
function normaliseFile(f: SubjectFile): ResolvedFile {
  return {
    name: f.file_name ?? f.name ?? "Archivo",
    uuid: f.uuid ?? undefined,
    url: f.original_url ?? f.url ?? undefined,
  };
}

export default function SuspensionExtensionLog({
  log,
}: SuspensionExtensionLogProps) {
  const title = getLogTitle(log);
  const status = getStatus(log);
  const ctx = log.properties?.context;
  const commentStyle = getCommentStyle(log);
  const scaledToUser = ctx?.scaled_to_user;
  const approvedDays = getApprovedDays(log);
  const unit = getUnit(log);

  // ── Files: prefer ctx.files, fall back to subject.files ──────────────────
  // Only show files for "created" status (solicited by the applicant)
  const subjectFiles: SubjectFile[] = Array.isArray(log.subject?.files)
    ? log.subject!.files
    : [];
  const ctxFiles = ctx?.files ?? [];
  const rawFiles =
    ctxFiles.length > 0 ? ctxFiles : subjectFiles.map(normaliseFile);

  // Only show files when status is "created" (applicant's log)
  const resolvedFiles: ResolvedFile[] = status === "created" ? rawFiles : [];

  // ── Comments: only from log.properties.context.comment_text ─────────────
  const ctxCommentText: string | null = ctx?.comment_text ?? null;
  const comments: { text: string; author: string }[] = ctxCommentText
    ? [{ text: ctxCommentText, author: "" }]
    : [];

  const commentLabel = ctx?.comment_label || commentStyle.label;

  const hasStatusRow =
    status === "approved" ||
    status === "denied" ||
    (status === "scaled" && !!scaledToUser);

  const hasCard =
    hasStatusRow || comments.length > 0 || resolvedFiles.length > 0;

  return (
    <div className="flex-1 pb-6">
      {/* Title */}
      <h3 className="mb-2">{title}</h3>

      {/* ── Unified card: status + comment + files ────────────────────────── */}
      {hasCard && (
        <div className="flex flex-col mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
          {/* Approved */}
          {status === "approved" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-body">Marcado como</span>
              <Badge size="xs" variant="success">
                Aprobado
                <Icon
                  type="fas"
                  name="check"
                  size="xs"
                  className="text-success-quiet"
                />
              </Badge>
              {approvedDays != null && (
                <>
                  <span className="text-sm text-body">Tiempo otorgado</span>
                  <Badge size="xs" variant="warning">
                    {approvedDays} {unit}
                    <Icon
                      type="fas"
                      name="clock"
                      size="xs"
                      className="text-warning-quiet"
                    />
                  </Badge>
                </>
              )}
            </div>
          )}

          {/* Denied */}
          {status === "denied" && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-body">Marcado como</span>
              <Badge size="xs" variant="danger">
                Denegado
                <Icon
                  type="fas"
                  name="x"
                  size="xs"
                  className="text-danger-quiet"
                />
              </Badge>
            </div>
          )}

          {/* Scaled — recipient */}
          {status === "scaled" && scaledToUser && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-body">Enviado a</span>
              <Avatar className="shrink-0" size="xs">
                <AvatarFallback color="action">
                  {scaledToUser.name?.charAt(0).toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
              <span className="text-action">
                {`${scaledToUser.name} ${scaledToUser.last_name}`.trim()}
              </span>
            </div>
          )}

          {/* ── Files (inside card, above comment) ───────────────────────── */}
          {resolvedFiles.length > 0 && (
            <>
              {hasStatusRow && <hr className="border-neutral-quiet -mx-2" />}
              <div className="flex items-center gap-2 flex-wrap">
                {resolvedFiles.map((file, idx) => (
                  <LogFile key={`file-${idx}-${file.uuid}`} file={file} />
                ))}
              </div>
            </>
          )}

          {/* Comments */}
          {comments.length > 0 && resolvedFiles.length > 0 && (
            <hr className="border-neutral-quiet -mx-2" />
          )}
          {comments.map((c, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Icon
                  type="fas"
                  name="comment"
                  className={`${commentStyle.iconColor} text-sm`}
                />
                <span
                  className={`text-sm font-semibold ${commentStyle.labelColor}`}
                >
                  {commentLabel}
                </span>
              </div>
              <span className="text-sm text-body">{c.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Author */}
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm text-body">Por</span>
        <Avatar className="shrink-0" size="xs">
          <AvatarFallback color="action">
            {log.author?.charAt(0).toUpperCase() || "?"}
          </AvatarFallback>
        </Avatar>
        <span className="text-action">{log.author}</span>
      </div>

      {/* Date */}
      <div className="text-sm text-body-quiet capitalize">
        {formatDate(log.date)}
      </div>
    </div>
  );
}

const LogFile = ({ file }: { file: ResolvedFile }) => {
  const { data, isPending } = useGetFile(file.uuid ?? "");

  const downloadFile = () => {
    if (!data) return;
    window.open(data.data.url, "_blank", "noopener,noreferrer");
  };

  if (isPending) {
    return (
      <div className="min-w-[90px] min-h-[24px] flex items-center gap-1.5 px-2 py-2 rounded-lg border border-neutral-quiet bg-neutral-object hover:border-action transition-colors cursor-pointer">
        <Icon
          type="fas"
          name="spinner-third"
          size="sm"
          className="animate-spin text-neutral-quiet"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-neutral-quiet bg-neutral-object hover:border-action transition-colors cursor-pointer">
        No se pudo cargar el archivo
      </div>
    );
  }

  return (
    <button
      type="button"
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-neutral-quiet bg-neutral-object hover:border-action transition-colors cursor-pointer"
      onClick={() => downloadFile()}
    >
      <Icon
        type="fas"
        name="file-lines"
        className="text-action-quiet text-sm"
      />
      <span className="text-sm text-body">{file.name}</span>
    </button>
  );
};
